import { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    club_name: "", contact_name: "", email: "", phone: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#1D3557]/10 text-[#1D3557] px-4 py-3 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#1D3557] focus:border-[#1D3557] placeholder:text-[#1D3557]/25 transition-colors";

  if (submitted) {
    return (
      <section id="contact" data-testid="contact-section" className="py-10 lg:py-14 px-6 bg-white border-t border-[#1D3557]/5">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
            <PaperPlaneTilt size={36} weight="duotone" className="text-[#E63946] mb-5" />
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-[#1D3557] mb-3">Message Sent</h2>
            <p className="font-body text-base text-[#1D3557]/55 leading-relaxed" data-testid="contact-success-msg">
              Thanks for your enquiry. We'll be in touch within 24 hours to get your club set up.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-testid="contact-section" className="py-10 lg:py-14 px-6 bg-white border-t border-[#1D3557]/5">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-4">Get In Touch</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-[#1D3557] mb-5">
              Have Questions?
            </h2>
            <p className="font-body text-base text-[#1D3557]/55 leading-relaxed max-w-md">
              Want to know more about how TargetFunder can help your club? Drop us a message and we'll get back to you within 24 hours.
            </p>
            <div className="mt-6 hidden lg:block overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1692247754505-ef883766d0ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxzcG9ydCUyMGZhbnMlMjBoYW5kcyUyMHVwJTIwY3Jvd2QlMjBjZWxlYnJhdGlvbiUyMGdlbmVyaWN8ZW58MHx8fHwxNzc2NDM1NjI1fDA&ixlib=rb-4.1.0&q=85"
                alt="Fans cheering at a sporting event"
                className="w-full h-40 object-cover border border-[#1D3557]/8 opacity-80"
              />
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Club Name *</label>
                <input type="text" name="club_name" required value={form.club_name} onChange={handleChange} placeholder="e.g. Ballymore FC" className={inputClass} data-testid="contact-club-name" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Contact Name *</label>
                <input type="text" name="contact_name" required value={form.contact_name} onChange={handleChange} placeholder="Your full name" className={inputClass} data-testid="contact-name" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Email *</label>
                <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@yourclub.com" className={inputClass} data-testid="contact-email" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Phone (optional)</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+44 / +353" className={inputClass} data-testid="contact-phone" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#1D3557]/50 mb-1.5">Message *</label>
                <textarea name="message" required rows={3} value={form.message} onChange={handleChange} placeholder="Tell us about your club..." className={`${inputClass} resize-none`} data-testid="contact-message" />
              </div>
              {error && <p className="text-[#E63946] text-sm font-body" data-testid="contact-error">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full bg-[#E63946] text-white font-bold uppercase tracking-widest px-8 py-3.5 hover:bg-[#d32f3c] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : <><PaperPlaneTilt size={16} weight="bold" />Send Enquiry</>}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
